import { toast } from 'react-toastify'
import { CustomCell, GridCellKind, ProvideEditorCallback, type CustomRenderer } from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'

import { IAttachmentCell } from '@/core/models/columns/attachment'
import { STORAGE_KEYS } from '@/shared/constants'
import { FileManager } from '@/components/FileManager/FileManager'

import { getEditorStyle } from '../helpers/editor-style-helper'

/*
 * This file defines a custom renderer for file attachments in a data grid.
 * It includes functionality for displaying file icons, handling file uploads,
 * and providing interactive elements like download, copy, edit, and delete.
 */

// Interfaces
interface CustomFileCellProps {
  readonly type: ColumnTypes
  readonly cell: IAttachmentCell
}

export type CustomFileCell = CustomCell<CustomFileCellProps>

interface Icon {
  symbol: string
  x: number
  width: number
  action: string
  color?: string
}

// Constants
const MAX_FILENAME_LENGTH = 30
const ICON_FONT = '24px Material Symbols Rounded'
const HOVER_ICON_FONT = '16px Material Symbols Rounded'

/*
 * FILE_TYPE_ICONS: A mapping of file extensions to their corresponding
 * Material Icons. This is used to display appropriate icons for different file types.
 *
 * The structure is optimized to group multiple file extensions that share the same icon,
 * reducing redundancy and improving readability.
 */
const FILE_TYPE_ICONS = {
  // Document types
  description: ['doc', 'docx', 'odt', 'rtf', 'txt'],

  // Spreadsheet types
  table_chart: ['xls', 'xlsx', 'ods', 'csv'],

  // Presentation types
  slideshow: ['ppt', 'pptx', 'odp'],

  // Image types
  image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'svg'],

  // Audio types
  audio_file: ['mp3', 'wav', 'ogg', 'flac', 'm4a'],

  // Video types
  video_file: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'],

  // Archive types
  folder_zip: ['zip', 'rar', '7z', 'tar', 'gz'],

  // Code and markup types
  code: ['html', 'css', 'js', 'json', 'xml'],

  // Database types
  database: ['sql', 'db', 'dbf'],

  // E-book types
  book: ['epub', 'mobi'],

  // CAD types
  architecture: ['dwg', 'dxf'],

  // Vector graphics
  draw: ['ai', 'eps'],

  // Font types
  font_download: ['ttf', 'otf', 'woff'],

  // PDF type
  picture_as_pdf: ['pdf'],
}

// Utility Functions

/*
 * getFileTypeIcon: Determines the appropriate icon for a given filename
 * based on its extension. Returns a default icon if the extension is not recognized.
 */
const getFileTypeIcon = (fileName: string): string => {
  if (!fileName || fileName.trim() === '') return 'upload'
  const extension = fileName.split('.').pop()?.toLowerCase() || ''

  for (const [icon, extensions] of Object.entries(FILE_TYPE_ICONS)) {
    if (extensions.includes(extension)) {
      return icon
    }
  }

  return 'insert_drive_file' // Default icon
}

/*
 * truncateFilename: Shortens a filename if it exceeds MAX_FILENAME_LENGTH,
 * adding an ellipsis (...) at the end.
 */
const truncateFilename = (filename: string): string => {
  return filename.length <= MAX_FILENAME_LENGTH ? filename : `${filename.slice(0, MAX_FILENAME_LENGTH - 3)}...`
}

// Editor Component

/*
 * Editor: A component that provides a file upload interface when editing a cell.
 * It uses the FileManager component to handle file uploads.
 */
const Editor: ReturnType<ProvideEditorCallback<CustomFileCell>> = ({ value, onFinishedEditing }) => {
  const { cell } = value.data

  const handleFileChange = (_type: any, urlFile: unknown) => {
    if (!urlFile || typeof urlFile !== 'string') return

    const updatedCell = cell.toCell(urlFile) as IAttachmentCell
    onFinishedEditing({
      ...value,
      data: { type: value.data.type, cell: updatedCell },
      displayData: updatedCell.toString(),
      copyData: updatedCell.toString(),
    } as CustomFileCell)
  }

  return (
    <div className='bg-white dark:bg-slate-800 dark:text-zinc-50 border bdr'>
      <FileManager modal={false} id='mk-gg-file-editor' show={true} onChange={handleFileChange} />
    </div>
  )
}

// Drawing Functions

/*
 * drawFileIcon: Renders the file type icon in the cell.
 */
const drawFileIcon = (ctx: CanvasRenderingContext2D, icon: string, x: number, y: number, theme: any) => {
  ctx.font = ICON_FONT
  ctx.fillStyle = theme.textDark
  ctx.textBaseline = 'alphabetic'
  ctx.fillText(icon, x, y)
}

/*
 * drawFileName: Renders the filename (or a placeholder if empty) in the cell.
 */
const drawFileName = (ctx: CanvasRenderingContext2D, fileName: string, x: number, y: number, theme: any) => {
  ctx.font = theme.baseFontFull
  ctx.fillStyle = theme.textDark
  ctx.textBaseline = 'alphabetic'
  ctx.fillText(fileName, x, y)
}

/*
 * drawActionIcons: Renders action icons (download, copy, edit, delete) when hovering over a non-empty cell.
 */
const drawActionIcons = (ctx: CanvasRenderingContext2D, icons: Icon[], y: number) => {
  ctx.font = HOVER_ICON_FONT
  icons.forEach((icon) => {
    if (icon.color) {
      ctx.fillStyle = icon.color
    }
    ctx.fillText(icon.symbol, icon.x, y)
  })
}

/*
 * drawFile: The main drawing function that composes the cell's appearance.
 * It calls other drawing functions to render different parts of the cell.
 */
export const drawFile = (args: any, fullFileName: string) => {
  const { theme, rect, ctx, hoverAmount } = args
  const initialSettings = {
    fillStyle: ctx.fillStyle,
    font: ctx.font,
    textAlign: ctx.textAlign,
    textBaseline: ctx.textBaseline,
  }

  // const fullFileName = cell.data.cell.fileName
  const isEmptyFile = !fullFileName || fullFileName.trim() === ''
  const fileName = isEmptyFile ? '' : truncateFilename(fullFileName)

  const verticalCenter = rect.y + rect.height / 2

  const fileIcon = getFileTypeIcon(fullFileName)
  const iconX = rect.x + theme.cellHorizontalPadding
  const iconY = verticalCenter + ctx.measureText(fileIcon).actualBoundingBoxAscent / 2 + 10

  drawFileIcon(ctx, fileIcon, iconX, iconY, theme)

  const textX = iconX + ctx.measureText(fileIcon).width + 8
  const textY = verticalCenter + ctx.measureText(fileName).actualBoundingBoxAscent / 2 - 5

  drawFileName(ctx, fileName, textX, textY, theme)

  if (!isEmptyFile && hoverAmount > 0) {
    const hoverIconY = verticalCenter + ctx.measureText('download').actualBoundingBoxAscent / 2 + 4
    const icons: Icon[] = [
      {
        symbol: 'download',
        x: textX + ctx.measureText(fileName).width + 10,
        width: 20,
        action: 'download',
      },
      {
        symbol: 'content_copy',
        x: textX + ctx.measureText(fileName).width + 35,
        width: 20,
        action: 'copy',
      },
      { symbol: 'edit', x: textX + ctx.measureText(fileName).width + 60, width: 20, action: 'edit' },
      {
        symbol: 'delete',
        x: textX + ctx.measureText(fileName).width + 85,
        width: 20,
        action: 'delete',
      },
    ]
    drawActionIcons(ctx, icons, hoverIconY)
  }

  Object.assign(ctx, initialSettings)
}

// Click Handling

/*
 * onClickIcon: Determines which action to take based on where the user clicked in the cell.
 * Returns the action to be performed (e.g., 'download', 'copy', 'edit', 'delete').
 */
const onClickIcon = (args: any): string | undefined => {
  const { posX: clickX, bounds: rect, cell, theme } = args

  const fullFileName = cell.data.cell.fileName
  const isEmptyFile = !fullFileName || fullFileName.trim() === ''

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d', { alpha: false })
  if (!ctx) return

  ctx.font = ICON_FONT
  const iconX = rect.x + theme.cellHorizontalPadding
  const iconWidth = ctx.measureText('upload').width

  ctx.font = theme.baseFontFull
  const textX = iconX + iconWidth + 8
  const textWidth = ctx.measureText(isEmptyFile ? '' : truncateFilename(fullFileName)).width

  const clickArea = textX + textWidth

  if (rect.x + clickX <= clickArea) {
    return isEmptyFile ? 'add_file' : 'edit'
  }

  if (!isEmptyFile) {
    const icons: Icon[] = [
      { symbol: 'download', x: textX + textWidth + 10, width: 20, action: 'download' },
      { symbol: 'content_copy', x: textX + textWidth + 35, width: 20, action: 'copy' },
      { symbol: 'edit', x: textX + textWidth + 60, width: 20, action: 'edit' },
      { symbol: 'delete', x: textX + textWidth + 85, width: 20, action: 'delete' },
    ]

    const rectClickX = rect.x + clickX
    for (const icon of icons) {
      if (rectClickX >= icon.x && rectClickX < icon.x + icon.width) {
        return icon.action
      }
    }
  }

  return undefined
}

// Main Renderer

/*
 * CustomFileRenderer: The main renderer object that defines how the custom file cell
 * should be rendered, edited, and interacted with in the data grid.
 */
const CustomFileRenderer: CustomRenderer<CustomFileCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomFileCell =>
    (cell.data as CustomFileCellProps).type === ColumnTypes.Attachment,
  draw: (args, cell) => {
    const fullFileName = cell.data.cell.fileName
    drawFile(args, fullFileName)
  },
  provideEditor: () => {
    const fileManagerTab = localStorage.getItem(STORAGE_KEYS.FILE_MGR_TAB)

    let editorHeight = 0
    if (fileManagerTab === 'upload' || fileManagerTab === 'fileUrl') {
      editorHeight = 112
    } else if (fileManagerTab === 'myFiles') {
      editorHeight = 470
    }

    const style = {
      maxWidth: '600px',
      width: '600px',
      overflow: 'hidden',
      boxShadow: 'unset',
      backgroundColor: 'transparent',
    }
    let customEditorHeight = getEditorStyle(editorHeight, true)

    return {
      disablePadding: true,
      disableStyling: false,
      styleOverride: { ...style, ...customEditorHeight },
      editor: Editor,
    }
  },
  measure: (ctx, cell: any, theme) =>
    ctx.measureText(decodeURI(cell.data.cell.fileName.slice(0, 34)) ?? cell.data).width +
    theme.cellHorizontalPadding * 2 +
    140,
  onPaste: (toPaste, cell) => {
    const uri = (() => {
      try {
        return new URL(toPaste.trim()).toString()
      } catch {
        return undefined
      }
    })()
    return { ...cell, data: uri || '' }
  },
  onDelete: (cellData) => {
    toast.error('File deleted')
    return { ...cellData, cell: cellData.data.cell.toCell('') as IAttachmentCell }
  },
  onClick: (args: any): CustomFileCell | undefined => {
    const attachmentCell = args.cell.data.cell as IAttachmentCell
    const iconClicked = onClickIcon(args)

    switch (iconClicked) {
      case 'edit':
      case 'add_file':
        return undefined
      case 'download':
        args.preventDefault()
        window.open(attachmentCell.toString(), '_blank')
        break
      case 'copy':
        args.preventDefault()
        navigator.clipboard
          .writeText(attachmentCell.toString())
          .then(() => toast.success('URL copied to clipboard'))
          .catch((err) => console.error('Failed to copy URL: ', err))
        break
      case 'delete':
        args.preventDefault()
        const dataEditorRef = args.cell.data.extra.dataEditorRef.current
        attachmentCell.toCell('')
        dataEditorRef.emit('delete')
        break
      default:
        args.preventDefault()
        return undefined
    }
  },
  needsHover: true,
}

export default CustomFileRenderer
